import { Course } from '../courses/types';

export const WEBGL_3D_GRAPHICS: Course = {
  id: 'webgl-3d-graphics',
  title: 'WebGL and 3D Graphics Programming',
  description: 'Master 3D graphics programming with WebGL, Three.js, and advanced rendering techniques.',
  level: 'advanced',
  duration: '6-8 weeks',
  estimatedHours: 40,
  tags: ['WebGL', 'Three.js', '3D Graphics', 'Shaders', 'OpenGL'],
  prerequisites: ['JavaScript proficiency', 'Linear algebra basics', 'Computer graphics fundamentals'],
  learningOutcomes: [
    'Master WebGL API and graphics pipeline',
    'Create complex 3D scenes with Three.js',
    'Write custom shaders and materials',
    'Implement advanced lighting and effects',
    'Optimize 3D applications for web',
    'Build interactive 3D experiences'
  ],
  modules: [
    {
      id: 'webgl-fundamentals',
      title: 'WebGL Fundamentals',
      description: 'Learn the WebGL API and graphics programming basics.',
      duration: '3 hours',
      difficulty: 'advanced',
      content: `
# WebGL Fundamentals

## Graphics Pipeline Overview

### WebGL Context and Setup
\`\`\`javascript
// Setting up WebGL context
function initWebGL(canvas) {
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    
    if (!gl) {
        throw new Error('WebGL not supported');
    }
    
    // Set viewport
    gl.viewport(0, 0, canvas.width, canvas.height);
    
    // Enable depth testing
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    
    // Set clear color
    gl.clearColor(0.0, 0.0, 0.0, 1.0);
    
    return gl;
}

// Basic render loop
function render(gl) {
    // Clear the canvas
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    
    // Draw operations here
    
    requestAnimationFrame(() => render(gl));
}
\`\`\`

## Shader Programming

### Vertex Shader
\`\`\`glsl
// Basic vertex shader
attribute vec3 a_position;
attribute vec3 a_normal;
attribute vec2 a_texCoord;

uniform mat4 u_modelMatrix;
uniform mat4 u_viewMatrix;
uniform mat4 u_projectionMatrix;
uniform mat4 u_normalMatrix;

varying vec3 v_position;
varying vec3 v_normal;
varying vec2 v_texCoord;

void main() {
    vec4 worldPosition = u_modelMatrix * vec4(a_position, 1.0);
    gl_Position = u_projectionMatrix * u_viewMatrix * worldPosition;
    
    v_position = worldPosition.xyz;
    v_normal = (u_normalMatrix * vec4(a_normal, 0.0)).xyz;
    v_texCoord = a_texCoord;
}
\`\`\`

### Fragment Shader
\`\`\`glsl
// Basic fragment shader with lighting
precision mediump float;

varying vec3 v_position;
varying vec3 v_normal;
varying vec2 v_texCoord;

uniform vec3 u_lightPosition;
uniform vec3 u_lightColor;
uniform vec3 u_cameraPosition;
uniform sampler2D u_texture;

void main() {
    vec3 normal = normalize(v_normal);
    vec3 lightDirection = normalize(u_lightPosition - v_position);
    vec3 viewDirection = normalize(u_cameraPosition - v_position);
    vec3 reflectDirection = reflect(-lightDirection, normal);
    
    // Ambient lighting
    vec3 ambient = 0.1 * u_lightColor;
    
    // Diffuse lighting
    float diff = max(dot(normal, lightDirection), 0.0);
    vec3 diffuse = diff * u_lightColor;
    
    // Specular lighting
    float spec = pow(max(dot(viewDirection, reflectDirection), 0.0), 32.0);
    vec3 specular = 0.5 * spec * u_lightColor;
    
    // Sample texture
    vec4 textureColor = texture2D(u_texture, v_texCoord);
    
    vec3 result = (ambient + diffuse + specular) * textureColor.rgb;
    gl_FragColor = vec4(result, textureColor.a);
}
\`\`\`

## Buffer Management

### Vertex Buffer Objects
\`\`\`javascript
class GeometryBuffer {
    constructor(gl, vertices, indices, normals, texCoords) {
        this.gl = gl;
        this.vertexCount = indices.length;
        
        // Create vertex buffer
        this.vertexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STATIC_DRAW);
        
        // Create index buffer
        this.indexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);
        
        // Create normal buffer
        this.normalBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.normalBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(normals), gl.STATIC_DRAW);
        
        // Create texture coordinate buffer
        this.texCoordBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(texCoords), gl.STATIC_DRAW);
    }
    
    bind(program) {
        const gl = this.gl;
        
        // Bind vertex positions
        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        const positionLocation = gl.getAttribLocation(program, 'a_position');
        gl.enableVertexAttribArray(positionLocation);
        gl.vertexAttribPointer(positionLocation, 3, gl.FLOAT, false, 0, 0);
        
        // Bind normals
        gl.bindBuffer(gl.ARRAY_BUFFER, this.normalBuffer);
        const normalLocation = gl.getAttribLocation(program, 'a_normal');
        gl.enableVertexAttribArray(normalLocation);
        gl.vertexAttribPointer(normalLocation, 3, gl.FLOAT, false, 0, 0);
        
        // Bind texture coordinates
        gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
        const texCoordLocation = gl.getAttribLocation(program, 'a_texCoord');
        gl.enableVertexAttribArray(texCoordLocation);
        gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);
        
        // Bind index buffer
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
    }
    
    draw() {
        this.gl.drawElements(this.gl.TRIANGLES, this.vertexCount, this.gl.UNSIGNED_SHORT, 0);
    }
}
\`\`\`

## Matrix Mathematics

### Camera and Projection
\`\`\`javascript
class Camera {
    constructor(fov, aspect, near, far) {
        this.position = [0, 0, 5];
        this.target = [0, 0, 0];
        this.up = [0, 1, 0];
        
        this.projectionMatrix = this.createPerspectiveMatrix(fov, aspect, near, far);
        this.viewMatrix = this.createViewMatrix();
    }
    
    createPerspectiveMatrix(fov, aspect, near, far) {
        const f = Math.tan(Math.PI * 0.5 - 0.5 * fov);
        const rangeInv = 1.0 / (near - far);
        
        return [
            f / aspect, 0, 0, 0,
            0, f, 0, 0,
            0, 0, (near + far) * rangeInv, -1,
            0, 0, near * far * rangeInv * 2, 0
        ];
    }
    
    createViewMatrix() {
        const zAxis = this.normalize(this.subtract(this.position, this.target));
        const xAxis = this.normalize(this.cross(this.up, zAxis));
        const yAxis = this.normalize(this.cross(zAxis, xAxis));
        
        return [
            xAxis[0], yAxis[0], zAxis[0], 0,
            xAxis[1], yAxis[1], zAxis[1], 0,
            xAxis[2], yAxis[2], zAxis[2], 0,
            -this.dot(xAxis, this.position),
            -this.dot(yAxis, this.position),
            -this.dot(zAxis, this.position),
            1
        ];
    }
    
    // Vector math utilities
    normalize(v) {
        const length = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
        return [v[0] / length, v[1] / length, v[2] / length];
    }
    
    cross(a, b) {
        return [
            a[1] * b[2] - a[2] * b[1],
            a[2] * b[0] - a[0] * b[2],
            a[0] * b[1] - a[1] * b[0]
        ];
    }
    
    dot(a, b) {
        return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    }
    
    subtract(a, b) {
        return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Understand WebGL graphics pipeline',
        'Write vertex and fragment shaders',
        'Manage buffers and textures',
        'Implement camera and projection systems'
      ]
    },
    {
      id: 'threejs-mastery',
      title: 'Three.js Mastery',
      description: 'Build complex 3D applications with the Three.js library.',
      duration: '4 hours',
      difficulty: 'intermediate',
      content: `
# Three.js Mastery

## Scene Setup and Management

### Advanced Scene Configuration
\`\`\`javascript
class ThreeJSApp {
    constructor(container) {
        this.container = container;
        this.scene = new THREE.Scene();
        this.camera = null;
        this.renderer = null;
        this.controls = null;
        
        this.init();
        this.setupLighting();
        this.setupControls();
        this.animate();
    }
    
    init() {
        // Camera setup
        const aspect = this.container.clientWidth / this.container.clientHeight;
        this.camera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);
        this.camera.position.set(5, 5, 5);
        
        // Renderer setup
        this.renderer = new THREE.WebGLRenderer({ 
            antialias: true,
            alpha: true 
        });
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.outputEncoding = THREE.sRGBEncoding;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1;
        
        this.container.appendChild(this.renderer.domElement);
        
        // Handle resize
        window.addEventListener('resize', () => this.onWindowResize(), false);
    }
    
    setupLighting() {
        // Ambient light
        const ambientLight = new THREE.AmbientLight(0x404040, 0.4);
        this.scene.add(ambientLight);
        
        // Directional light with shadows
        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(10, 10, 5);
        directionalLight.castShadow = true;
        directionalLight.shadow.mapSize.width = 2048;
        directionalLight.shadow.mapSize.height = 2048;
        directionalLight.shadow.camera.near = 0.5;
        directionalLight.shadow.camera.far = 500;
        this.scene.add(directionalLight);
        
        // Point light for accent
        const pointLight = new THREE.PointLight(0xff6000, 2, 100);
        pointLight.position.set(10, 10, 10);
        this.scene.add(pointLight);
        
        // Environment map
        const cubeTextureLoader = new THREE.CubeTextureLoader();
        const environmentMap = cubeTextureLoader.load([
            'textures/environmentMaps/px.jpg', // positive x
            'textures/environmentMaps/nx.jpg', // negative x
            'textures/environmentMaps/py.jpg', // positive y
            'textures/environmentMaps/ny.jpg', // negative y
            'textures/environmentMaps/pz.jpg', // positive z
            'textures/environmentMaps/nz.jpg'  // negative z
        ]);
        this.scene.environment = environmentMap;
    }
    
    setupControls() {
        this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.screenSpacePanning = false;
        this.controls.minDistance = 1;
        this.controls.maxDistance = 100;
        this.controls.maxPolarAngle = Math.PI / 2;
    }
    
    onWindowResize() {
        this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    }
    
    animate() {
        requestAnimationFrame(() => this.animate());
        
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }
}
\`\`\`

## Advanced Materials and Shaders

### Custom Shader Materials
\`\`\`javascript
class CustomShaderMaterial {
    constructor() {
        this.vertexShader = \`
            varying vec2 vUv;
            varying vec3 vPosition;
            varying vec3 vNormal;
            
            uniform float time;
            uniform float amplitude;
            
            void main() {
                vUv = uv;
                vNormal = normal;
                
                // Vertex displacement
                vec3 pos = position;
                pos.z += sin(pos.x * 10.0 + time) * amplitude;
                pos.z += sin(pos.y * 10.0 + time * 1.5) * amplitude;
                
                vPosition = pos;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
            }
        \`;
        
        this.fragmentShader = \`
            varying vec2 vUv;
            varying vec3 vPosition;
            varying vec3 vNormal;
            
            uniform float time;
            uniform vec3 color1;
            uniform vec3 color2;
            uniform sampler2D texture1;
            
            void main() {
                // Animated UV coordinates
                vec2 animatedUv = vUv + sin(time * 2.0) * 0.1;
                
                // Gradient based on position
                float gradient = sin(vPosition.x * 5.0 + time) * 0.5 + 0.5;
                
                // Mix colors
                vec3 color = mix(color1, color2, gradient);
                
                // Sample texture
                vec4 textureColor = texture2D(texture1, animatedUv);
                
                // Combine
                gl_FragColor = vec4(color * textureColor.rgb, 1.0);
            }
        \`;
        
        this.material = new THREE.ShaderMaterial({
            vertexShader: this.vertexShader,
            fragmentShader: this.fragmentShader,
            uniforms: {
                time: { value: 0 },
                amplitude: { value: 0.1 },
                color1: { value: new THREE.Color(0xff6000) },
                color2: { value: new THREE.Color(0x0060ff) },
                texture1: { value: null }
            }
        });
    }
    
    update(deltaTime) {
        this.material.uniforms.time.value += deltaTime;
    }
}
\`\`\`

## Physics Integration

### Cannon.js Physics
\`\`\`javascript
class PhysicsWorld {
    constructor(scene) {
        this.scene = scene;
        this.world = new CANNON.World();
        this.world.gravity.set(0, -9.82, 0);
        this.world.broadphase = new CANNON.NaiveBroadphase();
        
        this.bodies = [];
        this.meshes = [];
        
        this.setupGround();
    }
    
    setupGround() {
        // Physics ground
        const groundShape = new CANNON.Plane();
        const groundBody = new CANNON.Body({ mass: 0 });
        groundBody.addShape(groundShape);
        groundBody.quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), -Math.PI / 2);
        this.world.add(groundBody);
        
        // Visual ground
        const groundGeometry = new THREE.PlaneGeometry(100, 100);
        const groundMaterial = new THREE.MeshLambertMaterial({ color: 0x777777 });
        const groundMesh = new THREE.Mesh(groundGeometry, groundMaterial);
        groundMesh.rotation.x = -Math.PI / 2;
        groundMesh.receiveShadow = true;
        this.scene.add(groundMesh);
    }
    
    addBox(position, size) {
        // Physics body
        const shape = new CANNON.Box(new CANNON.Vec3(size.x/2, size.y/2, size.z/2));
        const body = new CANNON.Body({ mass: 1 });
        body.addShape(shape);
        body.position.set(position.x, position.y, position.z);
        this.world.add(body);
        
        // Visual mesh
        const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
        const material = new THREE.MeshLambertMaterial({ 
            color: Math.random() * 0xffffff 
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        this.scene.add(mesh);
        
        this.bodies.push(body);
        this.meshes.push(mesh);
        
        return { body, mesh };
    }
    
    addSphere(position, radius) {
        // Physics body
        const shape = new CANNON.Sphere(radius);
        const body = new CANNON.Body({ mass: 1 });
        body.addShape(shape);
        body.position.set(position.x, position.y, position.z);
        this.world.add(body);
        
        // Visual mesh
        const geometry = new THREE.SphereGeometry(radius, 32, 32);
        const material = new THREE.MeshLambertMaterial({ 
            color: Math.random() * 0xffffff 
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        this.scene.add(mesh);
        
        this.bodies.push(body);
        this.meshes.push(mesh);
        
        return { body, mesh };
    }
    
    update(deltaTime) {
        this.world.step(deltaTime);
        
        // Sync visual meshes with physics bodies
        for (let i = 0; i < this.bodies.length; i++) {
            this.meshes[i].position.copy(this.bodies[i].position);
            this.meshes[i].quaternion.copy(this.bodies[i].quaternion);
        }
    }
}
\`\`\`

## Animation and Interaction

### Advanced Animation System
\`\`\`javascript
class AnimationManager {
    constructor() {
        this.mixer = null;
        this.actions = {};
        this.clock = new THREE.Clock();
    }
    
    loadAnimations(model) {
        if (model.animations && model.animations.length > 0) {
            this.mixer = new THREE.AnimationMixer(model.scene);
            
            model.animations.forEach((clip) => {
                const action = this.mixer.clipAction(clip);
                this.actions[clip.name] = action;
            });
        }
    }
    
    play(animationName, loop = true, fadeInTime = 0.3) {
        const action = this.actions[animationName];
        if (action) {
            if (loop) {
                action.setLoop(THREE.LoopRepeat);
            }
            action.fadeIn(fadeInTime);
            action.play();
        }
    }
    
    stop(animationName, fadeOutTime = 0.3) {
        const action = this.actions[animationName];
        if (action) {
            action.fadeOut(fadeOutTime);
        }
    }
    
    crossFade(from, to, duration = 1.0) {
        const fromAction = this.actions[from];
        const toAction = this.actions[to];
        
        if (fromAction && toAction) {
            fromAction.fadeOut(duration);
            toAction.reset().fadeIn(duration).play();
        }
    }
    
    update() {
        if (this.mixer) {
            this.mixer.update(this.clock.getDelta());
        }
    }
}

// Raycasting for interaction
class InteractionManager {
    constructor(camera, scene) {
        this.camera = camera;
        this.scene = scene;
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        this.interactableObjects = [];
        
        this.setupEventListeners();
    }
    
    setupEventListeners() {
        window.addEventListener('mousemove', (event) => this.onMouseMove(event));
        window.addEventListener('click', (event) => this.onClick(event));
    }
    
    addInteractable(object, callbacks = {}) {
        object.userData.callbacks = callbacks;
        this.interactableObjects.push(object);
    }
    
    onMouseMove(event) {
        this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
        
        this.raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = this.raycaster.intersectObjects(this.interactableObjects);
        
        // Reset all objects
        this.interactableObjects.forEach(obj => {
            if (obj.userData.callbacks.onHoverExit) {
                obj.userData.callbacks.onHoverExit();
            }
        });
        
        // Handle hover
        if (intersects.length > 0) {
            const object = intersects[0].object;
            if (object.userData.callbacks.onHover) {
                object.userData.callbacks.onHover();
            }
        }
    }
    
    onClick(event) {
        this.raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = this.raycaster.intersectObjects(this.interactableObjects);
        
        if (intersects.length > 0) {
            const object = intersects[0].object;
            if (object.userData.callbacks.onClick) {
                object.userData.callbacks.onClick();
            }
        }
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Master Three.js scene management',
        'Create custom shader materials',
        'Integrate physics simulation',
        'Build interactive 3D applications'
      ]
    },
    {
      id: 'advanced-rendering',
      title: 'Advanced Rendering Techniques',
      description: 'Implement cutting-edge rendering effects and optimizations.',
      duration: '3 hours',
      difficulty: 'advanced',
      content: `
# Advanced Rendering Techniques

## Post-Processing Effects

### Custom Post-Processing Pipeline
\`\`\`javascript
class PostProcessingPipeline {
    constructor(renderer, scene, camera) {
        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;
        
        this.composer = new THREE.EffectComposer(renderer);
        this.setupPasses();
    }
    
    setupPasses() {
        // Render pass
        const renderPass = new THREE.RenderPass(this.scene, this.camera);
        this.composer.addPass(renderPass);
        
        // SSAO pass
        const ssaoPass = new THREE.SSAOPass(this.scene, this.camera, window.innerWidth, window.innerHeight);
        ssaoPass.kernelRadius = 16;
        ssaoPass.minDistance = 0.005;
        ssaoPass.maxDistance = 0.1;
        this.composer.addPass(ssaoPass);
        
        // Bloom pass
        const bloomPass = new THREE.UnrealBloomPass(
            new THREE.Vector2(window.innerWidth, window.innerHeight),
            1.5, // strength
            0.4, // radius
            0.85 // threshold
        );
        this.composer.addPass(bloomPass);
        
        // Custom shader pass
        const customPass = new THREE.ShaderPass({
            uniforms: {
                tDiffuse: { value: null },
                time: { value: 0 },
                distortion: { value: 0.1 }
            },
            vertexShader: \`
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            \`,
            fragmentShader: \`
                uniform sampler2D tDiffuse;
                uniform float time;
                uniform float distortion;
                varying vec2 vUv;
                
                void main() {
                    vec2 uv = vUv;
                    
                    // Chromatic aberration
                    float offset = distortion * 0.01;
                    vec4 color;
                    color.r = texture2D(tDiffuse, uv + vec2(offset, 0.0)).r;
                    color.g = texture2D(tDiffuse, uv).g;
                    color.b = texture2D(tDiffuse, uv - vec2(offset, 0.0)).b;
                    color.a = 1.0;
                    
                    // Vignette
                    float dist = distance(uv, vec2(0.5));
                    color.rgb *= 1.0 - smoothstep(0.3, 0.8, dist);
                    
                    gl_FragColor = color;
                }
            \`
        });
        this.composer.addPass(customPass);
        
        // Final output pass
        const outputPass = new THREE.OutputPass();
        this.composer.addPass(outputPass);
    }
    
    render(deltaTime) {
        this.composer.render(deltaTime);
    }
    
    setSize(width, height) {
        this.composer.setSize(width, height);
    }
}
\`\`\`

## Deferred Rendering

### G-Buffer Implementation
\`\`\`javascript
class DeferredRenderer {
    constructor(width, height) {
        this.width = width;
        this.height = height;
        
        this.setupGBuffer();
        this.setupLightingPass();
    }
    
    setupGBuffer() {
        // Create render targets for G-Buffer
        this.gBuffer = new THREE.WebGLMultipleRenderTargets(this.width, this.height, 4);
        
        // Position buffer (RGB: world position, A: depth)
        this.gBuffer.texture[0].name = 'gPosition';
        this.gBuffer.texture[0].type = THREE.FloatType;
        
        // Normal buffer (RGB: world normal, A: unused)
        this.gBuffer.texture[1].name = 'gNormal';
        this.gBuffer.texture[1].type = THREE.FloatType;
        
        // Albedo buffer (RGB: diffuse color, A: metallic)
        this.gBuffer.texture[2].name = 'gAlbedo';
        
        // Material properties (R: roughness, G: AO, B: emission, A: unused)
        this.gBuffer.texture[3].name = 'gMaterial';
        
        // G-Buffer material
        this.gBufferMaterial = new THREE.ShaderMaterial({
            vertexShader: \`
                varying vec3 vWorldPosition;
                varying vec3 vWorldNormal;
                varying vec2 vUv;
                
                void main() {
                    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                    vWorldPosition = worldPosition.xyz;
                    vWorldNormal = normalize(normalMatrix * normal);
                    vUv = uv;
                    
                    gl_Position = projectionMatrix * viewMatrix * worldPosition;
                }
            \`,
            fragmentShader: \`
                varying vec3 vWorldPosition;
                varying vec3 vWorldNormal;
                varying vec2 vUv;
                
                uniform sampler2D albedoMap;
                uniform sampler2D normalMap;
                uniform sampler2D roughnessMap;
                uniform sampler2D metallicMap;
                uniform sampler2D aoMap;
                
                void main() {
                    // Sample textures
                    vec4 albedo = texture2D(albedoMap, vUv);
                    vec3 normal = normalize(vWorldNormal);
                    float roughness = texture2D(roughnessMap, vUv).r;
                    float metallic = texture2D(metallicMap, vUv).r;
                    float ao = texture2D(aoMap, vUv).r;
                    
                    // Output to G-Buffer
                    gl_FragData[0] = vec4(vWorldPosition, gl_FragCoord.z);
                    gl_FragData[1] = vec4(normal * 0.5 + 0.5, 1.0);
                    gl_FragData[2] = vec4(albedo.rgb, metallic);
                    gl_FragData[3] = vec4(roughness, ao, 0.0, 1.0);
                }
            \`
        });
    }
    
    setupLightingPass() {
        this.lightingMaterial = new THREE.ShaderMaterial({
            uniforms: {
                gPosition: { value: this.gBuffer.texture[0] },
                gNormal: { value: this.gBuffer.texture[1] },
                gAlbedo: { value: this.gBuffer.texture[2] },
                gMaterial: { value: this.gBuffer.texture[3] },
                lightPositions: { value: [] },
                lightColors: { value: [] },
                lightIntensities: { value: [] },
                cameraPosition: { value: new THREE.Vector3() }
            },
            vertexShader: \`
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            \`,
            fragmentShader: \`
                uniform sampler2D gPosition;
                uniform sampler2D gNormal;
                uniform sampler2D gAlbedo;
                uniform sampler2D gMaterial;
                uniform vec3 lightPositions[8];
                uniform vec3 lightColors[8];
                uniform float lightIntensities[8];
                uniform vec3 cameraPosition;
                
                varying vec2 vUv;
                
                vec3 calculatePBR(vec3 worldPos, vec3 normal, vec3 albedo, float metallic, float roughness, vec3 lightPos, vec3 lightColor, float intensity) {
                    vec3 lightDir = normalize(lightPos - worldPos);
                    vec3 viewDir = normalize(cameraPosition - worldPos);
                    vec3 halfwayDir = normalize(lightDir + viewDir);
                    
                    float distance = length(lightPos - worldPos);
                    float attenuation = intensity / (distance * distance);
                    vec3 radiance = lightColor * attenuation;
                    
                    // Cook-Torrance BRDF
                    float NdotV = max(dot(normal, viewDir), 0.0);
                    float NdotL = max(dot(normal, lightDir), 0.0);
                    float HdotV = max(dot(halfwayDir, viewDir), 0.0);
                    float NdotH = max(dot(normal, halfwayDir), 0.0);
                    
                    vec3 F0 = mix(vec3(0.04), albedo, metallic);
                    vec3 F = F0 + (1.0 - F0) * pow(1.0 - HdotV, 5.0);
                    
                    float D = (roughness * roughness) / (3.14159265 * pow(NdotH * NdotH * (roughness * roughness - 1.0) + 1.0, 2.0));
                    float G = min(1.0, min(2.0 * NdotH * NdotV / HdotV, 2.0 * NdotH * NdotL / HdotV));
                    
                    vec3 numerator = D * G * F;
                    float denominator = 4.0 * NdotV * NdotL + 0.001;
                    vec3 specular = numerator / denominator;
                    
                    vec3 kS = F;
                    vec3 kD = vec3(1.0) - kS;
                    kD *= 1.0 - metallic;
                    
                    return (kD * albedo / 3.14159265 + specular) * radiance * NdotL;
                }
                
                void main() {
                    vec4 positionData = texture2D(gPosition, vUv);
                    vec4 normalData = texture2D(gNormal, vUv);
                    vec4 albedoData = texture2D(gAlbedo, vUv);
                    vec4 materialData = texture2D(gMaterial, vUv);
                    
                    vec3 worldPos = positionData.xyz;
                    vec3 normal = normalize(normalData.xyz * 2.0 - 1.0);
                    vec3 albedo = albedoData.rgb;
                    float metallic = albedoData.a;
                    float roughness = materialData.r;
                    float ao = materialData.g;
                    
                    vec3 color = vec3(0.0);
                    
                    // Calculate lighting for each light
                    for(int i = 0; i < 8; i++) {
                        color += calculatePBR(worldPos, normal, albedo, metallic, roughness, lightPositions[i], lightColors[i], lightIntensities[i]);
                    }
                    
                    // Apply ambient occlusion
                    color *= ao;
                    
                    gl_FragColor = vec4(color, 1.0);
                }
            \`
        });
    }
    
    render(scene, camera, lights) {
        // Geometry pass
        this.renderer.setRenderTarget(this.gBuffer);
        this.renderer.render(scene, camera);
        
        // Lighting pass
        this.updateLights(lights);
        this.renderer.setRenderTarget(null);
        this.renderer.render(this.lightingQuad, this.lightingCamera);
    }
    
    updateLights(lights) {
        const positions = [];
        const colors = [];
        const intensities = [];
        
        lights.forEach(light => {
            positions.push(light.position.x, light.position.y, light.position.z);
            colors.push(light.color.r, light.color.g, light.color.b);
            intensities.push(light.intensity);
        });
        
        this.lightingMaterial.uniforms.lightPositions.value = positions;
        this.lightingMaterial.uniforms.lightColors.value = colors;
        this.lightingMaterial.uniforms.lightIntensities.value = intensities;
    }
}
\`\`\`

## Performance Optimization

### Level of Detail (LOD) System
\`\`\`javascript
class LODManager {
    constructor(camera) {
        this.camera = camera;
        this.lodObjects = [];
        this.updateInterval = 100; // ms
        this.lastUpdate = 0;
    }
    
    addLODObject(object, distances, geometries) {
        const lodObject = {
            object: object,
            distances: distances,
            geometries: geometries,
            currentLOD: 0
        };
        
        this.lodObjects.push(lodObject);
        return lodObject;
    }
    
    update(currentTime) {
        if (currentTime - this.lastUpdate < this.updateInterval) return;
        
        this.lodObjects.forEach(lodObj => {
            const distance = this.camera.position.distanceTo(lodObj.object.position);
            let newLOD = lodObj.geometries.length - 1;
            
            for (let i = 0; i < lodObj.distances.length; i++) {
                if (distance < lodObj.distances[i]) {
                    newLOD = i;
                    break;
                }
            }
            
            if (newLOD !== lodObj.currentLOD) {
                lodObj.object.geometry = lodObj.geometries[newLOD];
                lodObj.currentLOD = newLOD;
            }
        });
        
        this.lastUpdate = currentTime;
    }
}

// Frustum culling optimization
class FrustumCuller {
    constructor(camera) {
        this.camera = camera;
        this.frustum = new THREE.Frustum();
        this.matrix = new THREE.Matrix4();
    }
    
    updateFrustum() {
        this.matrix.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse);
        this.frustum.setFromProjectionMatrix(this.matrix);
    }
    
    cullObjects(objects) {
        this.updateFrustum();
        
        objects.forEach(object => {
            if (object.geometry && object.geometry.boundingSphere) {
                const sphere = object.geometry.boundingSphere.clone();
                sphere.applyMatrix4(object.matrixWorld);
                
                object.visible = this.frustum.intersectsSphere(sphere);
            }
        });
    }
}
\`\`\`
      `,
      learningObjectives: [
        'Implement post-processing pipelines',
        'Create deferred rendering systems',
        'Optimize 3D performance',
        'Build advanced visual effects'
      ]
    }
  ]
};

export const MOBILE_GAME_DEVELOPMENT: Course = {
  id: 'mobile-game-development',
  title: 'Mobile Game Development',
  description: 'Create engaging mobile games for iOS and Android platforms.',
  level: 'intermediate',
  duration: '6-8 weeks',
  estimatedHours: 35,
  tags: ['Unity', 'Mobile', 'iOS', 'Android', 'Game Design'],
  prerequisites: ['Unity basics', 'C# programming', 'Mobile development concepts'],
  learningOutcomes: [
    'Build mobile games for iOS and Android',
    'Optimize games for mobile performance',
    'Implement touch controls and mobile UI',
    'Handle device-specific features',
    'Deploy games to app stores'
  ],
  modules: [
    {
      id: 'mobile-optimization',
      title: 'Mobile Game Optimization',
      description: 'Optimize games for mobile devices and performance.',
      duration: '2 hours',
      difficulty: 'intermediate',
      content: 'Mobile-specific optimization techniques, battery life, and performance profiling.',
      learningObjectives: [
        'Optimize for mobile hardware',
        'Manage battery consumption',
        'Handle different screen sizes',
        'Implement efficient rendering'
      ]
    },
    {
      id: 'touch-controls',
      title: 'Touch Controls and Mobile UI',
      description: 'Design intuitive touch-based game controls.',
      duration: '2 hours',
      difficulty: 'intermediate',
      content: 'Touch input handling, gesture recognition, and mobile-friendly UI design.',
      learningObjectives: [
        'Implement touch controls',
        'Design mobile UI layouts',
        'Handle gesture input',
        'Create responsive interfaces'
      ]
    }
  ]
};

export const GRAPHICS_PROGRAMMING_COURSES = [
  WEBGL_3D_GRAPHICS,
  MOBILE_GAME_DEVELOPMENT
];